import {SessionStorage} from '@chekin/core';
import type {FaceDetectionMechanism} from '../types';

const DEVICE_MEMORY_THRESHOLD_MB = 4;
const HARDWARE_CONCURRENCY_THRESHOLD = 4;
const SERVER_MODE_KEY = 'face_detection_use_server_mode';

interface NavigatorExtended extends Navigator {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
  };
}

interface CanUseClientSideDetectionOptions {
  forceMechanism?: FaceDetectionMechanism;
  setIsWebGLSupported?: (isSupported: boolean) => void;
}

export const canUseClientSideDetection = ({
  forceMechanism = 'AUTO',
  setIsWebGLSupported,
}: CanUseClientSideDetectionOptions = {}): boolean => {
  if (forceMechanism === 'CLIENT') return true;
  if (forceMechanism === 'SERVER') return false;

  const savedPreference = SessionStorage.get(SERVER_MODE_KEY);
  if (savedPreference !== null) {
    return savedPreference === 'false';
  }

  try {
    const extendedNavigator = navigator as NavigatorExtended;

    const hasGoodMemory =
      typeof extendedNavigator.deviceMemory === 'undefined' ||
      extendedNavigator.deviceMemory >= DEVICE_MEMORY_THRESHOLD_MB;

    const hasGoodCores =
      !('hardwareConcurrency' in extendedNavigator) ||
      extendedNavigator.hardwareConcurrency >= HARDWARE_CONCURRENCY_THRESHOLD;

    let webGLSupported = false;
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      webGLSupported = Boolean(gl);

      if (setIsWebGLSupported) {
        setIsWebGLSupported(webGLSupported);
      }
    } catch {
      webGLSupported = false;
      if (setIsWebGLSupported) {
        setIsWebGLSupported(false);
      }
    }

    const networkInfo = extendedNavigator.connection;
    const hasUsableNetwork = !networkInfo || networkInfo.effectiveType !== 'slow-2g';

    const canUseClientSide =
      hasGoodMemory && hasGoodCores && webGLSupported && hasUsableNetwork;

    SessionStorage.set(SERVER_MODE_KEY, (!canUseClientSide).toString());

    return canUseClientSide;
  } catch (error) {
    console.error('Error checking device capabilities:', error);
    return false;
  }
};
